/**
 * Favorites Storage Utilities for localStorage persistence
 */

import { FavoriteItem, FavoritesCollection, FavoriteType } from '@/types/favorites';

const STORAGE_KEY = 'v2v2-favorites';
const CURRENT_VERSION = 1;

/**
 * Save favorites to localStorage
 */
export function saveFavorites(items: FavoriteItem[]): void {
  try {
    const collection: FavoritesCollection = {
      items,
      lastUpdated: new Date().toISOString(),
      version: CURRENT_VERSION,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(collection));
  } catch (error) {
    console.error('Failed to save favorites to localStorage:', error);
    throw new Error('Unable to save favorites. Please check your storage permissions.');
  }
}

/**
 * Load favorites from localStorage
 */
export function loadFavorites(): FavoriteItem[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) {
      return [];
    }

    const collection = JSON.parse(data) as FavoritesCollection;
    
    // Validate collection structure
    if (!isValidFavoritesCollection(collection)) {
      console.warn('Invalid favorites collection found, resetting...');
      clearFavorites();
      return [];
    }

    return collection.items;
  } catch (error) {
    console.error('Failed to load favorites from localStorage:', error);
    return [];
  }
}

/**
 * Check if a collection is valid
 */
function isValidFavoritesCollection(data: unknown): data is FavoritesCollection {
  if (!data || typeof data !== 'object') return false;
  
  const collection = data as Record<string, unknown>;
  
  if (!Array.isArray(collection.items)) return false;
  if (typeof collection.lastUpdated !== 'string') return false;
  if (typeof collection.version !== 'number') return false;
  
  // Validate each item
  return collection.items.every((item) => 
    typeof item === 'object' &&
    typeof item.id === 'string' &&
    typeof item.type === 'string' &&
    typeof item.name === 'string' &&
    typeof item.slug === 'string' &&
    typeof item.addedAt === 'string' &&
    ['market', 'vendor', 'product'].includes(item.type)
  );
}

/**
 * Add a favorite item
 * Returns true if added, false if already exists
 */
export function addFavorite(item: FavoriteItem): boolean {
  const currentFavorites = loadFavorites();
  
  // Check if already exists
  if (currentFavorites.some((fav) => fav.id === item.id && fav.type === item.type)) {
    return false;
  }
  
  const updatedFavorites = [...currentFavorites, item];
  saveFavorites(updatedFavorites);
  return true;
}

/**
 * Remove a favorite item
 * Returns true if removed, false if not found
 */
export function removeFavorite(id: string, type: FavoriteType): boolean {
  const currentFavorites = loadFavorites();
  const initialLength = currentFavorites.length;
  
  const updatedFavorites = currentFavorites.filter(
    (item) => !(item.id === id && item.type === type)
  );
  
  if (updatedFavorites.length === initialLength) {
    return false;
  }
  
  saveFavorites(updatedFavorites);
  return true;
}

/**
 * Check if an item is favorited
 */
export function isFavorite(id: string, type: FavoriteType): boolean {
  const currentFavorites = loadFavorites();
  return currentFavorites.some((item) => item.id === id && item.type === type);
}

/**
 * Clear all favorites
 */
export function clearFavorites(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear favorites from localStorage:', error);
    throw new Error('Unable to clear favorites.');
  }
}

/**
 * Export favorites as JSON string
 */
export function exportFavorites(): string {
  const collection: FavoritesCollection = {
    items: loadFavorites(),
    lastUpdated: new Date().toISOString(),
    version: CURRENT_VERSION,
  };
  return JSON.stringify(collection, null, 2);
}

/**
 * Import favorites from JSON string
 * Returns imported items with validation
 */
export function importFavorites(json: string): { success: boolean; items: FavoriteItem[]; errors: string[] } {
  const errors: string[] = [];
  let items: FavoriteItem[] = [];

  try {
    const data = JSON.parse(json) as unknown;
    
    if (!isValidFavoritesCollection(data)) {
      return { success: false, items: [], errors: ['Invalid favorites format'] };
    }

    // Get current favorites to avoid duplicates
    const currentFavorites = loadFavorites();
    const existingIds = new Set(currentFavorites.map((f) => `${f.type}:${f.id}`));

    // Filter out duplicates and validate items
    items = data.items.filter((item) => {
      const key = `${item.type}:${item.id}`;
      if (existingIds.has(key)) {
        errors.push(`Skipped duplicate: ${item.name} (${item.type})`);
        return false;
      }
      return true;
    });

    // Merge with existing favorites
    const updatedFavorites = [...currentFavorites, ...items];
    saveFavorites(updatedFavorites);

    return { success: true, items, errors };
  } catch (error) {
    return { 
      success: false, 
      items: [], 
      errors: errors.length > 0 ? errors : ['Failed to parse favorites JSON'] 
    };
  }
}

/**
 * Create a FavoriteItem from add payload
 */
export function createFavoriteItem(payload: {
  id: string;
  type: FavoriteType;
  name: string;
  slug: string;
  metadata?: FavoriteItem['metadata'];
}): FavoriteItem {
  return {
    ...payload,
    addedAt: new Date().toISOString(),
  };
}

/**
 * Get count of favorites by type
 */
export function getFavoritesCountByType(items: FavoriteItem[]): Record<FavoriteType, number> {
  return {
    market: items.filter((item) => item.type === 'market').length,
    vendor: items.filter((item) => item.type === 'vendor').length,
    product: items.filter((item) => item.type === 'product').length,
  };
}
